"use client";

import { isOtherFeeTier } from "@/lib/pools/format-fee-tier";
import { computeTurnover } from "@/lib/pools/vol-tvl";

import type { Pool } from "../../data";
import { passesMarketCapRankFilter } from "../../market-cap-filter";
import type { PoolFilters } from "../../store/poolsStore";
import type { CoinPair } from "../../types";
import { normalizeTokenDisplaySymbol } from "../../utils/tokenIcons";
import { FILTER_TOOLTIPS, OTHER_FEE_TIER_FILTER_ID } from "./consts";

const aprCache = new Map<string, number>();
const formattedTvlCache = new Map<number, string>();
const formattedAprCache = new Map<string, string>();
const lowercaseCache = new Map<string, string>();
const feeTierCache = new Map<string, number>();
const tvlUsdFormatter = new Intl.NumberFormat("en-US", {
	style: "currency",
	currency: "USD",
	minimumFractionDigits: 0,
	maximumFractionDigits: 0,
});
const fullPercentFormatter = new Intl.NumberFormat("en-US", {
	minimumFractionDigits: 2,
	maximumFractionDigits: 2,
});

export const APR_1D_TOOLTIP =
	"The annualized return for the average liquidity provider in this pool, based on the past 24 hours of trade volume.";
export const APR_7D_TOOLTIP =
	"The annualized return for the average liquidity provider in this pool, based on the past 7 days of trade volume.";
export const APR_30D_TOOLTIP =
	"The annualized return for the average liquidity provider in this pool, based on the past 30 days of trade volume.";
export const VOL_TVL_1D_TOOLTIP = FILTER_TOOLTIPS.volTvl;
export const VOL_TVL_7D_TOOLTIP =
	"Dailyized capital turnover (7-day volume ÷ 7 ÷ current TVL). 1.00 means average daily volume equaled TVL. At the same fee tier, fee APR ≈ Vol/TVL × fee × 365.";
export const VOL_TVL_30D_TOOLTIP =
	"Dailyized capital turnover (30-day volume ÷ 30 ÷ current TVL). 1.00 means average daily volume equaled TVL. At the same fee tier, fee APR ≈ Vol/TVL × fee × 365.";

export const formatTvl = (tvl: number): string => {
	const cached = formattedTvlCache.get(tvl);
	if (cached !== undefined) return cached;

	const formatted = tvlUsdFormatter.format(tvl);
	formattedTvlCache.set(tvl, formatted);
	return formatted;
};

export const parseApr = (apr: string): number => {
	const cached = aprCache.get(apr);
	if (cached !== undefined) return cached;

	const value = parseFloat(apr.replace("%", "")) || 0;
	aprCache.set(apr, value);
	return value;
};

export const formatApr = (apr: string): string => {
	if (!apr) return apr;

	const cached = formattedAprCache.get(apr);
	if (cached !== undefined) return cached;

	const formatted = `${fullPercentFormatter.format(parseApr(apr))}%`;
	formattedAprCache.set(apr, formatted);
	return formatted;
};

export const clampTokenSymbol = (symbol: string): string => symbol.slice(0, 6);

export const formatTokenSymbol = (symbol: string): string => normalizeTokenDisplaySymbol(symbol);

export { isOtherFeeTier };

export const getLowercase = (str: string): string => {
	const cached = lowercaseCache.get(str);
	if (cached !== undefined) return cached;

	const lower = str.toLowerCase();
	lowercaseCache.set(str, lower);
	return lower;
};

export const parseFeeTier = (feeTier: string): number => {
	const cached = feeTierCache.get(feeTier);
	if (cached !== undefined) return cached;

	const value = parseFloat(feeTier.replace("%", "")) || 0;
	feeTierCache.set(feeTier, value);
	return value;
};

interface EnhancedPool extends Pool {
	_searchableText: string;
	_searchableTextLower: string;
}

interface PoolIndexes {
	byProtocol: Map<string, Set<number>>;
	byNetwork: Map<string, Set<number>>;
	byFeeTier: Map<string, Set<number>>;
	/** Pool indices with TVL strictly at or above each filter threshold (cumulative min). */
	byTvlMin: Map<string, Set<number>>;
	/** Pool indices with 1d volume at or above each filter threshold. */
	byVolume1dMin: Map<string, Set<number>>;
	/** Pool indices with dailyized Vol/TVL (1D) at or above each filter threshold. */
	byVolTvlMin: Map<string, Set<number>>;
	byCoinPair: Map<string, Set<number>>;
}

export const enhancePools = (pools: Pool[]): EnhancedPool[] => {
	return pools.map((pool) => {
		const tokenPair = `${pool.token1}/${pool.token2}`;
		const reverseTokenPair = `${pool.token2}/${pool.token1}`;
		const searchableText = `${pool.pool} ${pool.token1} ${pool.token2} ${tokenPair} ${reverseTokenPair}`;
		return {
			...pool,
			_searchableText: searchableText,
			_searchableTextLower: getLowercase(searchableText),
		};
	});
};

export const buildIndexes = (pools: Pool[]): PoolIndexes => {
	const byProtocol = new Map<string, Set<number>>();
	const byNetwork = new Map<string, Set<number>>();
	const byFeeTier = new Map<string, Set<number>>();
	const byTvlMin = new Map<string, Set<number>>();
	const byVolume1dMin = new Map<string, Set<number>>();
	const byVolTvlMin = new Map<string, Set<number>>();
	const byCoinPair = new Map<string, Set<number>>();

	pools.forEach((pool, index) => {
		if (!byProtocol.has(pool.uniswap)) byProtocol.set(pool.uniswap, new Set());
		byProtocol.get(pool.uniswap)!.add(index);

		if (!byNetwork.has(pool.network)) byNetwork.set(pool.network, new Set());
		byNetwork.get(pool.network)!.add(index);

		if (!byFeeTier.has(pool.feeTier)) byFeeTier.set(pool.feeTier, new Set());
		byFeeTier.get(pool.feeTier)!.add(index);
		if (isOtherFeeTier(pool.feeTier)) {
			if (!byFeeTier.has(OTHER_FEE_TIER_FILTER_ID))
				byFeeTier.set(OTHER_FEE_TIER_FILTER_ID, new Set());
			byFeeTier.get(OTHER_FEE_TIER_FILTER_ID)!.add(index);
		}

		if (pool.tvl >= 10_000) {
			if (!byTvlMin.has("10k")) byTvlMin.set("10k", new Set());
			byTvlMin.get("10k")!.add(index);
		}
		if (pool.tvl >= 100_000) {
			if (!byTvlMin.has("100k")) byTvlMin.set("100k", new Set());
			byTvlMin.get("100k")!.add(index);
		}
		if (pool.tvl >= 1_000_000) {
			if (!byTvlMin.has("1m")) byTvlMin.set("1m", new Set());
			byTvlMin.get("1m")!.add(index);
		}

		const vol = pool.volume1d ?? 0;
		if (vol >= 1_000) {
			if (!byVolume1dMin.has("1k")) byVolume1dMin.set("1k", new Set());
			byVolume1dMin.get("1k")!.add(index);
		}
		if (vol >= 10_000) {
			if (!byVolume1dMin.has("10k")) byVolume1dMin.set("10k", new Set());
			byVolume1dMin.get("10k")!.add(index);
		}
		if (vol >= 100_000) {
			if (!byVolume1dMin.has("100k")) byVolume1dMin.set("100k", new Set());
			byVolume1dMin.get("100k")!.add(index);
		}

		const turnover = computeTurnover(pool.tvl, pool.volume1d, "1d");
		if (turnover !== null) {
			if (turnover >= 0.05) {
				if (!byVolTvlMin.has("0.05")) byVolTvlMin.set("0.05", new Set());
				byVolTvlMin.get("0.05")!.add(index);
			}
			if (turnover >= 0.2) {
				if (!byVolTvlMin.has("0.2")) byVolTvlMin.set("0.2", new Set());
				byVolTvlMin.get("0.2")!.add(index);
			}
			if (turnover >= 0.5) {
				if (!byVolTvlMin.has("0.5")) byVolTvlMin.set("0.5", new Set());
				byVolTvlMin.get("0.5")!.add(index);
			}
			if (turnover >= 1) {
				if (!byVolTvlMin.has("1")) byVolTvlMin.set("1", new Set());
				byVolTvlMin.get("1")!.add(index);
			}
		}

		const pair1 = `${pool.token1Category}/${pool.token2Category}`;
		const pair2 = `${pool.token2Category}/${pool.token1Category}`;
		if (!byCoinPair.has(pair1)) byCoinPair.set(pair1, new Set());
		if (!byCoinPair.has(pair2)) byCoinPair.set(pair2, new Set());
		byCoinPair.get(pair1)!.add(index);
		byCoinPair.get(pair2)!.add(index);
	});

	return { byProtocol, byNetwork, byFeeTier, byTvlMin, byVolume1dMin, byVolTvlMin, byCoinPair };
};

/** Intersect two index sets by iterating the smaller one (no array clone). */
export function intersectSets(a: Set<number>, b: Set<number>): Set<number> {
	const [smaller, larger] = a.size <= b.size ? [a, b] : [b, a];
	const out = new Set<number>();
	for (const i of smaller) {
		if (larger.has(i)) out.add(i);
	}
	return out;
}

/** Union any number of index sets. */
export function unionSets(sets: Array<Set<number> | undefined>): Set<number> {
	const out = new Set<number>();
	for (const set of sets) {
		if (!set) continue;
		for (const i of set) out.add(i);
	}
	return out;
}

function cloneOrEmpty(source: Set<number> | undefined): Set<number> {
	return source ? new Set(source) : new Set();
}

export const filterPoolsOptimized = (
	enhancedPools: EnhancedPool[],
	indexes: PoolIndexes,
	filters: PoolFilters,
	selectedCoinPair: CoinPair | null,
): Pool[] => {
	const hasActiveFilters =
		selectedCoinPair !== null ||
		filters.keyword !== "" ||
		filters.protocol !== "all" ||
		filters.network.length > 0 ||
		filters.feeTier !== "all" ||
		filters.tvl !== "all" ||
		filters["1dVolume"] !== "all" ||
		filters.volTvl !== "all" ||
		filters.marketCap !== "all";

	if (!hasActiveFilters) {
		return enhancedPools;
	}

	let candidateIndices: Set<number> | null = null;

	if (filters.protocol !== "all") {
		candidateIndices = cloneOrEmpty(indexes.byProtocol.get(filters.protocol));
	}

	if (filters.network.length > 0) {
		const networkSet = unionSets(filters.network.map((name) => indexes.byNetwork.get(name)));
		if (candidateIndices) {
			candidateIndices = intersectSets(candidateIndices, networkSet);
		} else {
			candidateIndices = networkSet;
		}
	}

	if (filters.feeTier !== "all") {
		const feeTierSet = indexes.byFeeTier.get(filters.feeTier);
		if (candidateIndices) {
			candidateIndices = feeTierSet ? intersectSets(candidateIndices, feeTierSet) : new Set();
		} else {
			candidateIndices = cloneOrEmpty(feeTierSet);
		}
	}

	if (filters.tvl !== "all") {
		const tvlSet = indexes.byTvlMin.get(filters.tvl);
		if (candidateIndices) {
			candidateIndices = tvlSet ? intersectSets(candidateIndices, tvlSet) : new Set();
		} else {
			candidateIndices = cloneOrEmpty(tvlSet);
		}
	}

	if (filters["1dVolume"] !== "all") {
		const volSet = indexes.byVolume1dMin.get(filters["1dVolume"]);
		if (candidateIndices) {
			candidateIndices = volSet ? intersectSets(candidateIndices, volSet) : new Set();
		} else {
			candidateIndices = cloneOrEmpty(volSet);
		}
	}

	if (filters.volTvl !== "all") {
		const volTvlSet = indexes.byVolTvlMin.get(filters.volTvl);
		if (candidateIndices) {
			candidateIndices = volTvlSet ? intersectSets(candidateIndices, volTvlSet) : new Set();
		} else {
			candidateIndices = cloneOrEmpty(volTvlSet);
		}
	}

	if (selectedCoinPair) {
		const [firstCoin, secondCoin] = selectedCoinPair.pairs;
		const pair1 = `${firstCoin.symbol}/${secondCoin.symbol}`;
		const pair2 = `${secondCoin.symbol}/${firstCoin.symbol}`;
		const pairSet1 = indexes.byCoinPair.get(pair1);
		const pairSet2 = indexes.byCoinPair.get(pair2);
		const pairSet = new Set<number>();
		if (pairSet1) for (const i of pairSet1) pairSet.add(i);
		if (pairSet2) for (const i of pairSet2) pairSet.add(i);

		if (candidateIndices) {
			candidateIndices = intersectSets(candidateIndices, pairSet);
		} else {
			candidateIndices = pairSet;
		}
	}

	let candidates = candidateIndices
		? Array.from(candidateIndices).map((i) => enhancedPools[i])
		: enhancedPools;

	if (filters.marketCap !== "all") {
		candidates = candidates.filter((pool) => passesMarketCapRankFilter(pool, filters.marketCap));
	}

	if (filters.keyword) {
		const keywordLower = getLowercase(filters.keyword);
		return candidates.filter((pool) => pool._searchableTextLower.includes(keywordLower)) as Pool[];
	}

	return candidates as Pool[];
};
